import { Controller } from "@hotwired/stimulus";

import { DESKTOP_MIN_WIDTH, prefersReducedMotion } from "~/lib/viewport";
import { clamp01 } from "~/lib/web/ease";
import { registerScrub } from "~/lib/web/scrub";

/** How much of a buried card stays visible above the one covering it. */
const STRIP = 15;

const SCALE_STEP = 0.045;

const MAX_DEPTH = 3;

/** Fraction of the pin's travel by which the last card is in place. */
const STACK_END = 0.9;

/**
 * How long the cards keep their own compositor layer after the last scroll
 * event, so a continuous scroll does not promote and demote them per frame.
 */
const SETTLE_MS = 140;

/**
 * Where a card that has not arrived yet is held: below the pin's bottom edge,
 * not just its own height below the stack. The stack has a maximum height, so a
 * tall viewport leaves empty space under it, and a card held one card-height
 * down would show its top edge in that space. This offset also clears the
 * card's upward-cast shadow.
 */
const BELOW_PIN = 80;

export default class WebDeckController extends Controller<HTMLElement> {
  static override targets = ["runway", "pin", "stack", "card", "pill"];
  declare readonly runwayTarget: HTMLElement;
  declare readonly hasRunwayTarget: boolean;
  declare readonly pinTarget: HTMLElement;
  declare readonly hasPinTarget: boolean;
  declare readonly stackTarget: HTMLElement;
  declare readonly hasStackTarget: boolean;
  declare readonly cardTargets: HTMLElement[];
  declare readonly pillTargets: HTMLElement[];

  private settleTimer = 0;
  private unregister: (() => void) | null = null;
  private observer: ResizeObserver | null = null;
  private isDesktop = false;
  private below = 0;

  override connect(): void {
    if (
      !this.hasRunwayTarget ||
      !this.hasPinTarget ||
      !this.hasStackTarget ||
      !this.cardTargets.length
    ) {
      return;
    }
    /* A layout value that changes with the viewport rather than with the
       scroll. Measured here and on resize, so the scrub callback only writes,
       which is the split registerScrub documents. */
    this.measure();
    this.observer = new ResizeObserver(() => {
      this.measure();
      if (!this.isDesktop) this.clearCards();
    });
    this.observer.observe(this.pinTarget);

    this.unregister = registerScrub(this.runwayTarget, (rect, viewportHeight) =>
      this.scrub(rect, viewportHeight),
    );
  }

  override disconnect(): void {
    this.unregister?.();
    this.unregister = null;
    this.observer?.disconnect();
    this.observer = null;
    clearTimeout(this.settleTimer);
    this.settleTimer = 0;
    this.clearCards();
  }

  private measure(): void {
    this.isDesktop = window.innerWidth >= DESKTOP_MIN_WIDTH;
    this.below =
      this.pinTarget.clientHeight - this.stackTarget.offsetTop + BELOW_PIN;
  }

  private scrub(rect: DOMRect, viewportHeight: number): void {
    if (prefersReducedMotion() || !this.isDesktop) {
      // The viewport can cross back under the threshold with transforms still
      // on the cards.
      clearTimeout(this.settleTimer);
      this.clearCards();
      return;
    }
    this.holdLayersWhileMoving();

    // Standard sticky-pin maths: the pinned child holds still for exactly the
    // container's height minus one viewport, so that span is the travel.
    const travel = Math.max(1, rect.height - viewportHeight);
    const progress = clamp01(-rect.top / travel);
    const cards = this.cardTargets;
    const front = Math.min(1, progress / STACK_END) * (cards.length - 1);

    cards.forEach((card, index) => {
      const distance = front - index;
      let y: number;
      let scale: number;
      let buried: boolean;
      if (distance < 0) {
        // Still below, sliding up into place.
        y = Math.min(1, -distance) * this.below;
        scale = 1;
        buried = false;
      } else {
        const depth = Math.min(distance, MAX_DEPTH);
        y = -depth * STRIP;
        scale = 1 - SCALE_STEP * depth;
        // Still the front card until the next one has fully arrived.
        buried = distance >= 1;
      }
      // Whole pixels: a fractional offset makes the compositor resample the
      // card's whole raster, and a tenth of a pixel is not visible.
      card.style.transform = `translate3d(0, ${Math.round(y)}px, 0) scale(${scale.toFixed(4)})`;
      card.classList.toggle("is-buried", buried);
    });

    this.pillTargets.forEach((pill, index) => {
      const fill = pill.querySelector<HTMLElement>(".web-deck__pill-fill");
      if (fill === null) return;
      const amount = clamp01(front - index + 1);
      fill.style.clipPath = `inset(0 ${((1 - amount) * 100).toFixed(2)}% 0 0)`;
    });
  }

  private holdLayersWhileMoving(): void {
    this.hint("transform");
    clearTimeout(this.settleTimer);
    this.settleTimer = window.setTimeout(() => this.hint(""), SETTLE_MS);
  }

  private hint(value: string): void {
    for (const card of this.cardTargets) card.style.willChange = value;
  }

  private clearCards(): void {
    for (const card of this.cardTargets) {
      card.style.transform = "";
      card.style.willChange = "";
    }
  }
}
